import { Box, Button, MobileStepper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Application, ApplicationChecklist } from '../../data/types';
import { useRegistry } from '../../store/RegistryStore';
import { useInvestorScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { Card, InvestorTitle } from '../common';
import ApplyComplete from './ApplyComplete';
import { EntityStep, InvestmentStep, OwnersStep } from './ApplyStepsA';
import { BankStep, EligibilityStep, IdentityStep, ReviewStep, TaxStep } from './ApplyStepsB';
import { APPLY_STEPS, type ApplyForm } from './applyTypes';

const STEP_COMPONENTS = [
  InvestmentStep,
  EntityStep,
  OwnersStep,
  IdentityStep,
  TaxStep,
  BankStep,
  EligibilityStep,
  ReviewStep,
];
const CHECKLIST_BY_STEP: (keyof ApplicationChecklist | null)[] = [
  null,
  'entity',
  'owners',
  'identity',
  'tax',
  'bank',
  'wholesale',
  'signed',
];

export default function ApplyPage() {
  const { applicationId } = useParams();
  const [params] = useSearchParams();
  const scope = useInvestorScope();
  const { dispatch } = useRegistry();
  const navigate = useNavigate();
  const toast = useToast();
  const existing = scope.applications.find((item) => item.id === applicationId);
  const existingHolder = existing
    ? scope.holders.find((holder) => holder.investmentEntityId === existing.investmentEntityId)
    : undefined;

  const initial = useMemo<ApplyForm>(() => {
    const holder = existingHolder ?? scope.holders[0];
    const fundId = existing?.fundId ?? params.get('fund') ?? 'f-sdf2';
    return {
      fundId,
      classId:
        existing?.classId ??
        scope.classes.find((item) => item.fundId === fundId && item.open)?.id ??
        '',
      amount: existing ? String(existing.amount) : '250000',
      holderId: holder?.id ?? 'NEW',
      applicantType: holder?.type ?? 'INDIVIDUAL',
      applicantName: holder?.name ?? '',
      contactName: holder?.contactName ?? scope.login.name,
      email: holder?.email ?? scope.login.email,
      abn: holder?.abn ?? '',
      owners: (holder?.beneficialOwners ?? [])
        .filter((owner) => owner.role !== 'BENEFICIARY')
        .map((owner) => ({ name: owner.name, role: owner.role.toLowerCase() })),
      idVerified: existing?.checklist.identity === 'DONE',
      tfn: holder?.tfnStatus === 'QUOTED' || holder?.tfnStatus === 'ABN_QUOTED' ? 'on file' : '',
      residency: holder?.residency ?? 'AU',
      usPerson: false,
      bankName: holder?.bank.accountName ?? '',
      bsb: holder?.bank.bsb ?? '',
      account: holder?.bank.accountNumber ?? '',
      certificate: existing?.checklist.wholesale === 'DONE' ? 'on file' : '',
      signature: '',
      agree: false,
    };
    // Initial values only; later edits live in local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const firstPending = existing
    ? Math.max(
        0,
        CHECKLIST_BY_STEP.findIndex((key) => key && existing.checklist[key] !== 'DONE'),
      )
    : 0;
  const [step, setStep] = useState(firstPending);
  const [form, setForm] = useState<ApplyForm>(initial);
  const [appId] = useState<string>(() => existing?.id ?? `app-new-${Date.now().toString(36)}`);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const update = useCallback(
    (patch: Partial<ApplyForm>) => setForm((current) => ({ ...current, ...patch })),
    [],
  );

  const entityLinks = () => {
    if (existing)
      return {
        investmentEntityId: existing.investmentEntityId,
        allocationId: existing.allocationId,
      };
    const holder = scope.holders.find((item) => item.id === form.holderId);
    return holder ? { investmentEntityId: holder.investmentEntityId } : {};
  };

  const toApplication = (completedThrough: number): Partial<Application> & { id?: string } => {
    const checklist = {
      ...(existing?.checklist ?? {
        entity: 'PENDING',
        owners: 'PENDING',
        identity: 'PENDING',
        tax: 'PENDING',
        bank: 'PENDING',
        wholesale: 'PENDING',
        signed: 'PENDING',
        funds: 'PENDING',
      }),
    } as ApplicationChecklist;
    CHECKLIST_BY_STEP.forEach((key, index) => {
      if (key && index <= completedThrough) checklist[key] = 'DONE';
    });
    return {
      id: appId,
      fundId: form.fundId,
      classId: form.classId,
      ...entityLinks(),
      applicantName: form.applicantName || scope.login.name,
      applicantType: form.applicantType,
      contactName: form.contactName,
      email: form.email,
      amount: Number(form.amount) || 0,
      investorLoginId: scope.login.id,
      checklist,
    };
  };

  const saveProgress = (completedThrough: number) => {
    dispatch({
      type: 'INVESTOR_APPLICATION_SAVE',
      application: toApplication(completedThrough),
      submit: false,
    });
  };

  if (submitted) return <ApplyComplete applicationId={submitted} form={form} />;
  if (existing && !['INVITED', 'IN_PROGRESS'].includes(existing.status))
    return <ApplyComplete applicationId={existing.id} form={form} />;

  const StepComponent = STEP_COMPONENTS[step];
  const canContinue = (() => {
    if (step === 0) return Boolean(form.fundId && form.classId && Number(form.amount) > 0);
    if (step === 1) return form.applicantName.trim().length > 2;
    if (step === 3) return form.idVerified;
    if (step === 5) return Boolean(form.bankName && form.bsb && form.account);
    if (step === 6) return Boolean(form.certificate);
    if (step === 7) return form.agree && form.signature.trim().length > 2;
    return true;
  })();

  return (
    <>
      <InvestorTitle
        title="Application"
        subtitle={
          existing
            ? `${existing.reference} · progress is saved as you go`
            : 'Progress is saved as you go'
        }
      />
      <Stepper
        activeStep={step}
        alternativeLabel
        sx={{ mb: 3, display: { xs: 'none', md: 'flex' } }}
      >
        {APPLY_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Typography variant="overline" color="text.secondary" sx={{ display: { md: 'none' } }}>
        Step {step + 1} of {APPLY_STEPS.length} · {APPLY_STEPS[step]}
      </Typography>
      <Card sx={{ maxWidth: 720 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {APPLY_STEPS[step]}
        </Typography>
        <StepComponent form={form} update={update} existingHolder={form.holderId !== 'NEW'} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button disabled={step === 0} onClick={() => setStep((value) => value - 1)}>
            Back
          </Button>
          {step < APPLY_STEPS.length - 1 ? (
            <Button
              variant="contained"
              disabled={!canContinue}
              onClick={() => {
                saveProgress(step);
                setStep((value) => value + 1);
              }}
            >
              Save and continue
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!canContinue}
              onClick={() => {
                const payload = toApplication(step);
                dispatch({ type: 'INVESTOR_APPLICATION_SAVE', application: payload, submit: true });
                toast('Application submitted');
                setSubmitted(appId);
              }}
            >
              Sign and submit
            </Button>
          )}
        </Box>
      </Card>
      <MobileStepper
        variant="progress"
        steps={APPLY_STEPS.length}
        position="static"
        activeStep={step}
        nextButton={<span />}
        backButton={<span />}
        sx={{ maxWidth: 720, bgcolor: 'transparent', display: { md: 'none' }, mt: 1 }}
      />
      <Button sx={{ mt: 2 }} onClick={() => navigate('/investor')}>
        Save and finish later
      </Button>
    </>
  );
}
